/**
 * Wire types for the `messaging` module — derived field-by-field from
 * `serializeMessage` / `serializeConversation` / `serializeConversationRow` /
 * `serializeBroadcastMessage` (refactor/backend/src/modules/messaging/serializers.ts).
 * snake_case verbatim, no camelCase mapping layer — see the FIELD-NAMING
 * DECISION note in src/types/user.ts.
 *
 * `sender_details`/`participants_details` nest the full `User` (including
 * `current_balance` as a plain number, per types/user.ts).
 */
import type { User } from './user';

/** Django `messaging.models.BroadcastMessage.TARGET_GROUPS`. */
export const BROADCAST_TARGET_GROUPS = ['ALL_TENANTS', 'PROPERTY_TENANTS', 'ALL_AGENTS'] as const;
export type BroadcastTargetGroup = (typeof BROADCAST_TARGET_GROUPS)[number];

/** `MessageSerializer` — backend serializers.ts:86-103. `fields = '__all__'`. */
export interface Message {
  id: number;
  sender_details: User;
  text: string;
  image: string | null;
  created_at: string;
  is_read: boolean;
  read_at: string | null;
  conversation: number;
  sender: number;
}

/**
 * `ConversationSerializer` (detail) — backend serializers.ts:122-141.
 * `messages` is the FULL thread, oldest first.
 */
export interface Conversation {
  id: number;
  messages: Message[];
  participants_details: User[];
  last_message: Message | null;
  created_at: string;
  updated_at: string;
  participants: number[];
  unit: number | null;
  maintenance_request: number | null;
}

/**
 * `ConversationListSerializer` — backend serializers.ts:148-164. Carries only
 * the single most recent message, not the full thread.
 */
export interface ConversationRow {
  id: number;
  participants: number[];
  participants_details: User[];
  last_message: Message | null;
  updated_at: string;
  unit: number | null;
  maintenance_request: number | null;
}

/**
 * `BroadcastMessageSerializer` — backend serializers.ts:171-183. Explicit
 * `fields` tuple; `read_only_fields = ['sender', 'created_at']`.
 */
export interface BroadcastMessage {
  id: number;
  sender: number;
  sender_name: string;
  message: string;
  target_group: BroadcastTargetGroup;
  property: number | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Write requests
// ---------------------------------------------------------------------------

/** `conversationCreateSchema` on write (serializers.ts:195-199). */
export interface ConversationCreateRequest {
  participants: number[];
  unit?: number | null | undefined;
  maintenance_request?: number | null | undefined;
}

export type ConversationPatchRequest = Partial<ConversationCreateRequest>;

/**
 * `POST messaging/conversations/{id}/send_message/` body. No serializer in
 * Django — the view reads `text`/`image` straight off the body and 400s with
 * `{error: "Message text or image is required"}` when both are falsy.
 */
export interface SendMessageRequest {
  text?: string | undefined;
  image?: string | undefined;
}

/** `broadcastMessageCreateSchema` on write (serializers.ts:211-215). */
export interface BroadcastMessageCreateRequest {
  message: string;
  target_group: BroadcastTargetGroup;
  property?: number | null | undefined;
}

export type BroadcastMessagePatchRequest = Partial<BroadcastMessageCreateRequest>;
